import React, { useState, useContext } from "react";
import { styled, useTheme } from "@mui/material/styles";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell, { tableCellClasses } from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import Collapse from "@mui/material/Collapse";
import IconButton from "@mui/material/IconButton";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import TablePagination from "@mui/material/TablePagination";
import api from "../../../API/axiosInstance";
import { generateHash } from "../../../API/hashUtil";
import { MyContext } from "../../../MyContextApi";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faSquareFacebook,
  faYoutube,
  faInstagram,
  faSquareXTwitter,
  faSquareThreads,
  faReddit,
} from "@fortawesome/free-brands-svg-icons"; // Import the icons
import CircularProgress from "@mui/material/CircularProgress";
import { set } from "date-fns";

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    // background:
    //   " linear-gradient(179.2deg, rgb(73, 115, 158) 0%, rgb(83, 80, 151) 40%, rgb(86, 83, 132) 100%)",
    backgroundColor: "#1AACAC",
    color: theme.palette.common.white,
    textAlign: "center",
    border: "none", // Ensure consistent border style
    // border: `1px solid ${theme.palette.divider}`, // Ensure consistent border style
    borderWidth: "1px", // Same border width for all cells

    "&:first-of-type": {
      // borderBottomLeftRadius: "15px", // Rounded edges on the top-left corner
      // borderTopLeftRadius: "15px", // Rounded edges on the top-left corner
    },
    "&:last-of-type": {
      // borderBottomRightRadius: "15px", // Rounded edges on the top-right corner
      // borderTopRightRadius: "15px", // Rounded edges on the top-right corner
    },
    fontFamily: "'Host Grotesk', sans-serif", // Apply font
  },
  [`&.${tableCellClasses.body}`]: {
    fontFamily: "'Host Grotesk', sans-serif", // Apply font
    fontSize: 14,

    border: `1px solid ${theme.palette.divider}`, // Same border for body cells
    borderWidth: "1px", // Same border width for all cells
    backgroundColor: theme.palette.common.white,
  },
}));

const WhiteTableCell = styled(TableCell)(({ theme }) => ({
  backgroundColor: theme.palette.common.white,
  color: theme.palette.common.black,
  textAlign: "center",
  fontWeight: "bold",
  fontSize: "1rem", // Increased font size
}));

// Fixed order of sections
const sections = [
  "3(1)b(I)",
  "3(1)b(II)",
  "3(1)b(III)",
  "3(1)b(IV)",
  "3(1)b(V)",
  "3(1)b(VI)",
  "3(1)b(VII)",
  "3(1)b(VIII)",
  "3(1)b(IX)",
  "3(1)b(X)",
  "3(1)b(XI)",
];
const lowerSections = [
  "3(1)b(i)",
  "3(1)b(ii)",
  "3(1)b(iii)",
  "3(1)b(iv)",
  "3(1)b(v)",
  "3(1)b(vi)",
  "3(1)b(vii)",
  "3(1)b(viii)",
  "3(1)b(ix)",
  "3(1)b(x)",
  "3(1)b(xi)",
];
const descriptions = [
  "Belongs to another person and to which the user does not have any right",
  "Is obscene, pornographic, paedophilic, invasive of another‟s privacy including bodily privacy, insulting or harassing on the basis of gender, racially or ethnically objectionable, relating or encouraging money laundering or an online game that causes user harm, or promoting enmity between different groups on the grounds of religion or caste with the intent to incite violence;",
  "Is harmful to child",
  "Infringes any patent, trademark, copyright or other proprietary rights",
  "Deceives or misleads the addressee about the origin of the message or knowingly and intentionally communicates any misinformation or information which is patently false and untrue or misleading in nature or, in respect of any business of the Central Government, is identified as fake or false or misleading by such fact check unit of the Central Government as the Ministry may, by notification published in the Official Gazette, specify",
  "Impersonates another person",
  "Threatens the unity, integrity, defence, security or sovereignty of India, friendly relations with foreign States, or public order, or causes incitement to the commission of any cognisable offence, or prevents investigation of any offence, or is insulting other nation",
  "Contains software virus or any other computer code, file or program designed to interrupt, destroy or limit the functionality of any computer resource",
  "Is in the nature of an online game that is not verified as a permissible online game",
  "Is in the nature of advertisement or surrogate advertisement or promotion of an online game that is not a permissible online game, or of any online gaming intermediary offering such an online game",
  "Violates any law for the time being in force.",
];

function CollapsibleRow({
  section,
  count,
  description,
  startDateStr,
  endDateStr,
  color,
  isLowerCase, // New prop to indicate whether to use `lowerSections`
}) {
  // Select the appropriate section array based on context
  const displayedSection = isLowerCase
    ? lowerSections[sections.indexOf(section)]
    : section;

  const [open, setOpen] = useState(false);
  const [details, setDetails] = useState({});
  const [cellLoading, setCellLoading] = useState(false);
  const token = localStorage.getItem("Token");
  //
  const theme = useTheme(); // Use theme inside the component
  const { selectedRange } = useContext(MyContext);

  // Function to truncate text
  const truncateText = (text, maxLength = 80) => {
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + "...";
  };

  const handleClick = () => {
    let stringParams = {
      startDateStr: startDateStr ? startDateStr.toString() : "",
      endDateStr: endDateStr ? endDateStr.toString() : "",
      section: section ? section.toString() : "",
    };

    const hexHash = generateHash(stringParams);
    setOpen(!open);

    if (!open) {
      setCellLoading(true);
      api
        .get(`/api/sectionCounts`, {
          params: {
            section,
            startDateStr: startDateStr,
            endDateStr: endDateStr,
            HPPcode: hexHash,
          },
          headers: {
            // Security Headers
            "X-XSS-Protection": "1; mode=block",
            Authorization: `Bearer ${token}`,
            "X-Content-Type-Options": "nosniff",
            "X-Frame-Options": "DENY",
            "Referrer-Policy": "strict-origin-when-cross-origin",
            "Permissions-Policy": "camera=(), microphone=(), geolocation=()",

            // Standard Headers
            "Content-Type": "application/json",
          },
        })
        .then((response) => {
          setDetails(response.data);
          details.length > 0 ? setCellLoading(false) : setCellLoading(true);
          setCellLoading(false);
        })
        .catch((error) => {
          console.error("Error fetching data:", error);
          setDetails({});
          details.length > 0 ? setCellLoading(false) : setCellLoading(true);
          setCellLoading(false);
        });
    }
  };

const renderPlatformIcon = (platform) => {
  const normalizedPlatform = String(platform || "")
    .trim()
    .replace(/\.com$/i, "")
    .replace(/[_\-\s]+/g, "")
    .toUpperCase();

  switch (normalizedPlatform) {
    case "FACEBOOK":
      return (
        <FontAwesomeIcon
          icon={faSquareFacebook}
          style={{
            fontSize: "20px",
            color: "#4267B2",
            backgroundColor: "#E9F1FB",
            borderRadius: "50%",
            padding: "5px",
            paddingLeft: "7px",
            paddingRight: "7px",
            margin: "5px",
          }}
          title="Facebook"
        />
      );

    case "TWITTER":
    case "X":
      return (
        <FontAwesomeIcon
          icon={faSquareXTwitter}
          style={{
            fontSize: "20px",
            color: "black",
            backgroundColor: "#E1F5FE",
            borderRadius: "50%",
            padding: "5px",
            paddingLeft: "7px",
            paddingRight: "7px",
            margin: "5px",
          }}
          title="Twitter"
        />
      );

    case "YOUTUBE":
      return (
        <div
          style={{
            width: "30px",
            height: "30px",
            backgroundColor: "#FFEBEB",
            borderRadius: "50%",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            margin: "5px",
          }}
          title="YouTube"
        >
          <FontAwesomeIcon
            icon={faYoutube}
            style={{
              fontSize: "20px",
              color: "#FF0000",
            }}
          />
        </div>
      );

    case "THREADS":
      return (
        <FontAwesomeIcon
          icon={faSquareThreads}
          style={{
            fontSize: "20px",
            color: "black",
            backgroundColor: "#E1F5FE",
            borderRadius: "50%",
            padding: "5px",
            paddingLeft: "7px",
            paddingRight: "7px",
            margin: "5px",
          }}
          title="Threads"
        />
      );

    case "REDDIT":
      return (
        <FontAwesomeIcon
          icon={faReddit}
          style={{
            fontSize: "20px",
            color: "#ff4500",
            backgroundColor: "#E1F5FE",
            borderRadius: "50%",
            padding: "5px",
            paddingLeft: "7px",
            paddingRight: "7px",
            margin: "5px",
          }}
          title="Reddit"
        />
      );

    default:
      console.warn("Unknown platform from API:", platform);

      return (
        <span
          style={{
            margin: "0 5px",
            color: "rgba(0, 0, 0, 0.7)",
            fontStyle: "italic",
          }}
        >
          {platform || "Unknown"}
        </span>
      );
  }
};
  return (
    <React.Fragment>
      <TableRow>
        <StyledTableCell>
          <IconButton
            aria-label="expand row"
            size="small"
            onClick={handleClick}
          >
            {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
          <strong style={{ color: "#3A3845" }}> {displayedSection}</strong>
          <h5 style={{ color: color }}>
            {open ? description : truncateText(description)}
          </h5>
        </StyledTableCell>
        <StyledTableCell>{count}</StyledTableCell>
      </TableRow>

      <TableRow>
        <StyledTableCell
          style={{ paddingBottom: 0, paddingTop: 0 }}
          colSpan={6}
        >
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box sx={{ margin: 0, paddingBottom: "15px", paddingTop: "15px" }}>
              <Table size="small" aria-label="details">
                {cellLoading ? (
                  <CircularProgress sx={{ display: "block", margin: "auto" }} />
                ) : (
                  <>
                    {Object.keys(details).length > 0 && (
                      <TableHead
                        sx={{ border: `1px solid ${theme.palette.divider}` }}
                      >
                        {/* Add table head rows here if needed */}
                      </TableHead>
                    )}

                    <TableBody>
                      {Object.keys(details).length === 0 ? (
                        <TableRow>
                          <StyledTableCell colSpan={2}>
                            No Violated Content Found
                          </StyledTableCell>
                        </TableRow>
                      ) : (
                        Object.entries(details).map(([platform, count]) => (
                          <TableRow key={platform}>
                            <StyledTableCell>
                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                }}
                              >
                                {renderPlatformIcon(platform)}
                                <span style={{ marginLeft: "10px" }}>
                                  {platform.charAt(0).toUpperCase() +
                                    platform.slice(1).toLowerCase()}
                                </span>
                              </div>
                            </StyledTableCell>
                            <StyledTableCell>{count}</StyledTableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </>
                )}
              </Table>
            </Box>
          </Collapse>
        </StyledTableCell>
      </TableRow>
    </React.Fragment>
  );
}

const colors = [
  "#FF5733", // Bright Orange
  "#8A2BE2", // Blue Violet
  "#42E6A4", // Bright Green
  "#3357FF", // Bright Blue
  "#FF33A1", // Bright Pink
  "#FFD700", // Gold
  "#00CED1", // Dark Turquoise
  "#FF4500", // Orange Red
  "#36BA98", // Green Yellow
  "#836FFF", // Dark Violet
  "#1E90FF", // Dodger Blue
];

function SectionTable({ tableData, datestart, dateend }) {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(6);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  return (
    <Paper sx={{ outline: "none", borderRadius: "15px", boxShadow: "none" }}>
      <TableContainer component={Paper}>
        <Table
          stickyHeader
          aria-label="collapsible table"
          sx={{
            minWidth: 500,
            width: "100%",
            borderCollapse: "collapse",
            scrollbarWidth: "none",
          }}
        >
          <TableHead>
            <TableRow>
              <StyledTableCell>Section 3(1)b</StyledTableCell>
              <StyledTableCell>Count</StyledTableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sections
              .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
              .map((section, index) => {
                const absoluteIndex = page * rowsPerPage + index; // Calculate absolute index
                return (
                  <CollapsibleRow
                    key={section}
                    section={section}
                    description={descriptions[absoluteIndex]} // Use absolute index
                    count={tableData[section] || 0}
                    startDateStr={datestart}
                    endDateStr={dateend}
                    color={colors[absoluteIndex % colors.length]} // Map color using absolute index
                    isLowerCase={true} // Use `lowerSections` for this column
                  />
                );
              })}
          </TableBody>
        </Table>
      </TableContainer>
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          outline: "none",
          border: "none",
          backgroundColor: "white",
          borderBottomRightRadius: "15px",
          borderBottomLeftRadius: "15px",
        }}
      >
        <TablePagination
          rowsPerPageOptions={[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11]}
          component="div"
          count={sections.length} // Assuming sections.length is the total count
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          sx={{
            minWidth: "auto",
            // backgroundColor: "blue",
            height: "50px", // Adjust the height
            "& .MuiTablePagination-toolbar": {
              minHeight: "50px", // Ensures the toolbar matches the container height
              padding: "0 8px", // Reduce padding for a compact design
            },
            "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows":
              {
                fontSize: "15px", // Smaller font for text
                margin: 0, // Remove unnecessary margin
              },
            "& .MuiTablePagination-actions": {
              padding: 0, // Remove padding around actions
            },
            "& .MuiSelect-select": {
              fontSize: "15px", // Adjust font size of dropdown
              padding: "4px 8px", // Compact dropdown size
            },
          }} // Adjust the size of pagination box
        />
      </Box>
    </Paper>
  );
}

export default SectionTable;
